import { Box, styled } from "@mui/material";

import {
    PANEL_GAP,
    PANEL_WIDTH,
    sidePanelsActions,
    useDispatchSidePanels,
    useIsCollapsed,
    useOpenPanels,
} from "contexts/sidePanels";

import { WindowContentOnlyProvider } from "../windowRenderMode";
import { SIDE_PANEL_LAYOUT, SIDE_PANEL_Z_INDEX } from "./constants";
import { SidePanel } from "./SidePanel";

const Container = styled(Box)`
    position: absolute;
    right: ${SIDE_PANEL_LAYOUT.RIGHT_MARGIN}px;
    top: ${SIDE_PANEL_LAYOUT.TOP_MARGIN}px;
    bottom: ${SIDE_PANEL_LAYOUT.BOTTOM_MARGIN}px;
    width: ${PANEL_WIDTH}px;
    display: flex;
    flex-direction: column;
    gap: ${PANEL_GAP}px;
    z-index: ${SIDE_PANEL_Z_INDEX};
    pointer-events: none;
`;

interface PanelWrapperProps {
    collapsed?: boolean;
}

const PanelWrapper = styled(Box, {
    shouldForwardProp: (prop) => prop !== "collapsed",
})<PanelWrapperProps>`
    flex: ${({ collapsed }) => (collapsed ? "0 0 50%" : "1")};
    min-height: 0;
    display: flex;
    flex-direction: column;
`;

/**
 * Side Panel Container
 *
 * Container that renders the single open side panel.
 * The panel can be toggled between full-height and half-height (collapsed) mode.
 *
 * This is similar to WindowManager but for docked panels.
 */
export function SidePanelContainer() {
    const openPanels = useOpenPanels();
    const dispatch = useDispatchSidePanels();
    const isCollapsed = useIsCollapsed();

    if (openPanels.length === 0) {
        return null;
    }

    const handleClosePanel = (panelId: string) => {
        dispatch(sidePanelsActions.closePanel(panelId));
    };

    const handleToggleCollapse = () => {
        dispatch(sidePanelsActions.toggleCollapsed());
    };

    return (
        <Container>
            {openPanels.map((panel) => {
                const PanelComponent = panel.component;
                return (
                    <PanelWrapper key={panel.id} collapsed={isCollapsed}>
                        <SidePanel
                            title={panel.title}
                            icon={panel.icon}
                            onClose={() => handleClosePanel(panel.id)}
                            onToggleCollapse={handleToggleCollapse}
                            isCollapsed={isCollapsed}
                            hideHeader={panel.hideHeader}
                            data-testid={`side-panel-${panel.id}`}
                        >
                            <WindowContentOnlyProvider>
                                <PanelComponent onClose={() => handleClosePanel(panel.id)} />
                            </WindowContentOnlyProvider>
                        </SidePanel>
                    </PanelWrapper>
                );
            })}
        </Container>
    );
}
